import { useMemo } from "react";
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { BaseChartProps, ChartWrapper } from "./chart-utils";

import {
  chartColors,
  defaultResponsiveConfig,
  generateRandomData,
} from "./chart-helpers";

import {
  VerticalAlignmentType,
  HorizontalAlignmentType,
} from "recharts/types/component/DefaultLegendContent";

interface BarChartProps extends BaseChartProps {
  horizontal?: boolean;
  stacked?: boolean;
  barSize?: number;
  dataLabels?: boolean;
  dataLabelPosition?:
    | "top"
    | "center"
    | "bottom"
    | "insideTop"
    | "insideBottom";
  xAxisLabel?: string;
  yAxisLabel?: string;
}

export default function BarChart({
  data,
  series,
  title,
  subtitle,
  height = 300,
  width = "100%",
  className,
  colors = chartColors.series,
  showLegend = true,
  showTooltip = true,
  showGrid = true,
  animate = true,
  legendPosition = "bottom",
  horizontal = false,
  stacked = false,
  barSize,
  dataLabels = false,
  dataLabelPosition = "top",
  xAxisLabel,
  yAxisLabel,
}: BarChartProps) {
  const chartData = useMemo(() => {
    if (series && series.length > 0) {
      const categories = [
        ...new Set(series.flatMap((s) => s.data.map((d) => d.name))),
      ];
      return categories.map((category) => {
        const item: Record<string, string | number> = { name: category };
        series.forEach((s) => {
          const dataPoint = s.data.find((d) => d.name === category);
          item[s.name] = dataPoint ? dataPoint.value : 0;
        });
        return item;
      });
    }

    if (data) {
      return data.map((d) => ({ name: d.name, value: d.value }));
    }

    return generateRandomData(8, 100);
  }, [data, series]);

  const legendProps = useMemo(() => {
    const baseProps = {
      wrapperStyle: {
        paddingTop: legendPosition === "bottom" ? 20 : 0,
      },
    };

    switch (legendPosition) {
      case "top":
        return { ...baseProps, verticalAlign: "top" as VerticalAlignmentType };
      case "right":
        return {
          ...baseProps,
          verticalAlign: "middle" as VerticalAlignmentType,
          align: "right" as HorizontalAlignmentType,
        };
      case "left":
        return {
          ...baseProps,
          verticalAlign: "middle" as VerticalAlignmentType,
          align: "left" as HorizontalAlignmentType,
        };
      default:
        return {
          ...baseProps,
          verticalAlign: "bottom" as VerticalAlignmentType,
        };
    }
  }, [legendPosition]);

  const isMultiSeries = series && series.length > 0;
  const layout = horizontal ? "vertical" : "horizontal";

  return (
    <ChartWrapper
      title={title}
      subtitle={subtitle}
      className={className}
      height={height}
      width={width}
    >
      <ResponsiveContainer {...defaultResponsiveConfig}>
        <RechartsBarChart
          data={chartData}
          layout={layout}
          margin={{ top: 20, right: 30, left: 30, bottom: 20 }}
        >
          {showGrid && (
            <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
          )}
          <XAxis
            dataKey="name"
            type={horizontal ? "number" : "category"}
            tick={{ fill: chartColors.foreground }}
            stroke={chartColors.foreground}
            {...(horizontal && { type: "number", orientation: "bottom" })}
            {...(!horizontal && {
              label: xAxisLabel
                ? {
                    value: xAxisLabel,
                    position: "bottom",
                    offset: 0,
                    fill: chartColors.foreground,
                  }
                : undefined,
            })}
          />
          <YAxis
            {...(horizontal && { dataKey: "name", type: "category" })}
            {...(!horizontal && { type: "number" })}
            stroke={chartColors.foreground}
            tick={{ fill: chartColors.foreground }}
            label={
              (horizontal ? xAxisLabel : yAxisLabel)
                ? {
                    value: horizontal ? xAxisLabel : yAxisLabel,
                    angle: horizontal ? 0 : -90,
                    position: horizontal ? "bottom" : "left",
                    fill: chartColors.foreground,
                  }
                : undefined
            }
          />
          {showTooltip && <Tooltip cursor={{ fill: "rgba(0, 0, 0, 0.1)" }} />}
          {showLegend && <Legend {...legendProps} />}

          {isMultiSeries ? (
            series.map((s, idx) => (
              <Bar
                key={s.name}
                dataKey={s.name}
                fill={colors[idx % colors.length]}
                stackId={stacked ? "stack" : undefined}
                barSize={barSize}
                isAnimationActive={animate}
              >
                {dataLabels && (
                  <LabelList
                    dataKey={s.name}
                    position={dataLabelPosition}
                    fill={chartColors.foreground}
                    fontSize={12}
                  />
                )}
              </Bar>
            ))
          ) : (
            <Bar
              dataKey="value"
              fill={colors[0]}
              stackId={stacked ? "stack" : undefined}
              barSize={barSize}
              isAnimationActive={animate}
            >
              {dataLabels && (
                <LabelList
                  dataKey="value"
                  position={dataLabelPosition}
                  fill={chartColors.foreground}
                  fontSize={12}
                />
              )}
            </Bar>
          )}
        </RechartsBarChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
}
